/**
 * SERVICE QUẢN LÝ DỮ LIỆU YOUTUBE SHORTS (FRONTEND)
 * File: src/services/shortsService.js
 * Cung cấp API gọi dữ liệu video ngắn, cơ chế hàng đợi đệm (buffer),
 * chống trùng lặp (deduplicate) và tự động tải trước (prefetch) khi vuốt
 */

export class ShortsService {
  // Bộ nhớ đệm lưu các ID video đã xuất hiện trong phiên xem để chống trùng lặp
  static seenVideoIds = new Set();

  // Lưu trữ từ khóa truy vấn gần nhất
  static currentQuery = '';

  /**
   * Bóc tách mã videoId từ chuỗi nhập vào hoặc URL YouTube Shorts
   * @param {string} input Đường dẫn hoặc mã video
   * @returns {string|null} Mã video 11 ký tự hoặc null nếu không hợp lệ
   */
  static extractShortId(input) {
    if (!input || typeof input !== 'string') {
      return null;
    }
    const clean = input.trim();
    if (!clean) return null;

    // Kiểm tra định dạng youtube.com/shorts/<id>
    const shortsRegex = /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/;
    const match = clean.match(shortsRegex);
    if (match && match[1]) {
      return match[1];
    }

    // Nếu người dùng nhập trực tiếp 11 ký tự
    if (/^[a-zA-Z0-9_-]{11}$/.test(clean)) {
      return clean;
    }

    return null;
  }

  /**
   * Lấy danh sách video Shorts từ Backend API kèm cơ chế lọc trùng lặp
   * @param {string} [query=''] Từ khóa tìm kiếm (bỏ trống để lấy Shorts thịnh hành)
   * @param {number} [page=1] Số trang truy vấn
   * @returns {Promise<Array<{ videoId: string, title: string, thumbnail: string, viewCountText: string, author: string, type: string }>>}
   */
  static async getShortsFeed(query = '', page = 1) {
    // 1. Chuẩn hóa tham số đầu vào: mặc định ưu tiên thịnh hành Việt Nam
    const effectiveQuery = typeof query === 'string' && query.trim() ? query.trim() : 'thịnh hành việt nam';
    const pageNum = Math.max(1, Math.floor(Number(page) || 1));

    // Nếu chuyển sang từ khóa tìm kiếm mới, làm mới bộ đệm chống trùng
    if (effectiveQuery !== this.currentQuery) {
      this.currentQuery = effectiveQuery;
      this.seenVideoIds.clear();
    }

    const apiUrl = `/api/shorts-feed?q=${encodeURIComponent(effectiveQuery)}&page=${pageNum}&gl=VN&hl=vi`;

    // 2. Thiết lập timeout 6 giây để tránh treo giao diện
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    try {
      const response = await fetch(apiUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Máy chủ phản hồi mã lỗi HTTP ${response.status}`);
      }

      const json = await response.json();

      // 3. Kiểm tra tính hợp lệ của dữ liệu phản hồi
      if (!json || !json.success || !Array.isArray(json.data)) {
        return [];
      }

      // 4. Lọc các video hợp lệ và loại bỏ các video đã từng xuất hiện trong phiên
      const freshItems = [];
      for (const item of json.data) {
        if (item && item.videoId && typeof item.videoId === 'string') {
          if (!this.seenVideoIds.has(item.videoId)) {
            this.seenVideoIds.add(item.videoId);
            freshItems.push({
              videoId: item.videoId,
              title: item.title || 'Shorts Video',
              thumbnail: item.thumbnail || `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`,
              viewCountText: item.viewCountText || '',
              author: item.author || '',
              type: 'short'
            });
          }
        }
      }

      return freshItems;
    } catch (err) {
      clearTimeout(timeoutId);
      console.warn('Lỗi khi tải luồng Shorts từ máy chủ:', err.message);
      return [];
    }
  }

  /**
   * Lọc danh sách video loại bỏ trùng lặp và loại bỏ các mục không hợp lệ
   * @param {Array} items Danh sách video đầu vào
   * @returns {Array} Danh sách video duy nhất hợp lệ
   */
  static deduplicate(items) {
    if (!Array.isArray(items)) return [];
    const result = [];
    for (const item of items) {
      if (item && item.videoId && typeof item.videoId === 'string' && item.videoId.trim().length > 0) {
        if (!this.seenVideoIds.has(item.videoId)) {
          this.seenVideoIds.add(item.videoId);
          result.push({
            videoId: item.videoId,
            title: item.title || 'Shorts Video',
            thumbnail: item.thumbnail || `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`,
            viewCountText: item.viewCountText || '',
            author: item.author || '',
            type: 'short'
          });
        }
      }
    }
    return result;
  }

  /**
   * Alias phương thức lấy feed video Shorts
   * @param {string} [query='']
   * @param {number} [page=1]
   */
  static async getFeed(query = '', page = 1) {
    return this.getShortsFeed(query, page);
  }

  /**
   * Alias phương thức tải thêm video Shorts
   * @param {string} [query='']
   */
  static async fetchMore(query = '') {
    return this.getShortsFeed(query);
  }

  /**
   * Reset trạng thái bộ đệm khi người dùng muốn làm mới toàn bộ luồng
   */
  static resetFeed() {
    this.seenVideoIds.clear();
    this.currentQuery = '';
  }
}
